import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { FiChevronRight, FiPlus, FiX, FiCheck, FiEdit2 } from 'react-icons/fi';
import {
  listarAtendimentos, criarAtendimento, atualizarAtendimento, moverAtendimento, excluirAtendimento,
  listarEtapasDoAtendimento, adicionarEtapas, alternarEtapa, editarTextoEtapa, excluirEtapa,
} from '../../services/atendimentos';
import { listarClientes } from '../../services/clientes';
import { listarUsuarios } from '../../services/usuarios';
import { usuarioLogado } from '../../services/auth';
import styles from './Atendimento.module.css';
import useEsc from '../../hooks/useEsc';

/* Painel de triagem/atendimento ao cliente -- baseado no protótipo
   painel-essensials_8.html, com os dados vindos do banco (tabela
   "atendimentos", ver backend/sql/atendimentos.sql) em vez de localStorage.
   Cliente é sempre um dos nossos (busca/lista os cadastrados, sem texto
   livre) e atendente/setor vêm da lista de usuários que já existe
   (Configurações > Usuários) -- nada disso é recadastrado aqui. */

const ETAPAS = [
  { id: 'novo',        nome: 'Novos',             sub: 'sem dono' },
  { id: 'transferido', nome: 'Transferido',       sub: '' },
  { id: 'atend',       nome: 'Em atendimento',     sub: '' },
  { id: 'aguard',      nome: 'Aguardando cliente', sub: '' },
  { id: 'ok',          nome: 'Resolvidos',         sub: 'hoje' },
];

const PEDIDOS_SUGESTAO = [
  'Pediu 2ª via de boleto', 'Pediu guia de imposto', 'Pediu certidão negativa',
  'Pediu balancete', 'Dúvida sobre nota fiscal', 'Dúvida sobre folha de pagamento',
  'Admissão de colaborador', 'Rescisão de colaborador', 'Recebeu notificação e mandou',
  'Pediu parcelamento', 'Alteração cadastral', 'Cobrando retorno de pedido anterior',
  'Enviou documentos', 'Pediu declaração de faturamento',
];

/* setor não tem tabela própria (é texto livre em usuarios_sistema.setor,
   igual Grupos de Fluxo) -- a cor do card vem de um hash do nome do setor,
   assim qualquer setor novo cadastrado em Usuários já ganha cor sem
   precisar configurar nada aqui. */
const PALETA_SETOR = ['#2a78d6', '#eb6834', '#1baf7a', '#c98500', '#e0609e', '#008300', '#7c5cff', '#545b70'];
function corSetor(setor) {
  if (!setor) return '#b7b5aa';
  let h = 0;
  for (let i = 0; i < setor.length; i++) h = (h * 31 + setor.charCodeAt(i)) >>> 0;
  return PALETA_SETOR[h % PALETA_SETOR.length];
}

/* ---- relógio: só conta horário comercial, seg–sex 08h–18h (mesma conta do protótipo) ---- */
const EXP_INI = 8, EXP_FIM = 18;
function msUteis(ini, fim) {
  if (!(fim > ini)) return 0;
  let tot = 0, d = new Date(ini); d.setHours(0, 0, 0, 0);
  let guarda = 0;
  while (d.getTime() <= fim && guarda++ < 800) {
    const dow = d.getDay();
    if (dow >= 1 && dow <= 5) {
      const a = new Date(d); a.setHours(EXP_INI, 0, 0, 0);
      const b = new Date(d); b.setHours(EXP_FIM, 0, 0, 0);
      const x = Math.max(a.getTime(), ini), y = Math.min(b.getTime(), fim);
      if (y > x) tot += y - x;
    }
    d.setDate(d.getDate() + 1);
  }
  return tot;
}
function fmtDur(ms) {
  if (!ms || ms < 60000) return '0min';
  const min = Math.round(ms / 60000);
  if (min < 60) return min + 'min';
  const h = ms / 36e5;
  if (h < 10) { const hh = Math.floor(h), mm = Math.round((h - hh) * 60); return mm ? hh + 'h' + String(mm).padStart(2, '0') : hh + 'h'; }
  const dd = Math.floor(h / 10), rr = Math.round(h % 10);
  return dd + 'd' + (rr ? ' ' + rr + 'h' : '');
}
const zerado = () => ({ novo: 0, transferido: 0, atend: 0, aguard: 0 });
function correndo(item) {
  if (item.etapa === 'ok' || !item.etapa_desde) return 0;
  if (item.etapa === 'aguard') return 0;
  return msUteis(new Date(item.etapa_desde).getTime(), Date.now());
}
const tempoEtapa = item => (item.tempos?.[item.etapa] || 0) + correndo(item);
const tempoUtil = item => { const t = item.tempos || zerado(); return t.novo + (t.transferido || 0) + t.atend + correndo(item); };
function diasParado(item) {
  if (item.etapa !== 'aguard' || !item.etapa_desde) return 0;
  return Math.floor((Date.now() - new Date(item.etapa_desde).getTime()) / 864e5);
}
/* fecha o período corrente antes de mover de etapa -- devolve o "tempos"
   e "etapa_desde" já atualizados, prontos pra mandar no PATCH /mover */
function calcularFechamento(item) {
  const tempos = { ...(item.tempos || zerado()) };
  if (item.etapa_desde && item.etapa !== 'ok') {
    const ms = item.etapa === 'aguard'
      ? Date.now() - new Date(item.etapa_desde).getTime()
      : msUteis(new Date(item.etapa_desde).getTime(), Date.now());
    tempos[item.etapa] = (tempos[item.etapa] || 0) + ms;
  }
  return { tempos, etapa_desde: new Date().toISOString() };
}

const norm = s => String(s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').trim();
const iniciais = n => n ? n.trim().split(/\s+/).slice(0, 2).map(w => w[0]).join('').toUpperCase() : '';
const isoHoje = () => new Date().toISOString().slice(0, 10);

/* resumo do checklist interno (etapas) a partir da lista completa --
   usado tanto pro card (uma etapa de cada vez + barra) quanto pra
   sincronizar esse resumo de volta no array de cards depois de qualquer
   mudança feita no modal de etapas */
function resumoEtapas(lista) {
  const concluidas = lista.filter(e => e.concluida).length;
  const pendente = lista.find(e => !e.concluida);
  return {
    etapas_total: lista.length,
    etapas_concluidas: concluidas,
    etapa_atual: pendente?.texto ?? null,
    etapa_atual_id: pendente?.id ?? null,
  };
}

function relogioInfo(item) {
  if (item.etapa === 'ok') return { cor: 'var(--at-good)', t: '✓ ' + fmtDur(tempoUtil(item)), titulo: 'tempo útil total' };
  if (item.etapa === 'aguard') {
    const d = diasParado(item);
    const cor = d >= 7 ? 'var(--at-crit)' : d >= 3 ? 'var(--at-warn)' : 'var(--at-muted)';
    return { cor, t: '⏸ ' + (d <= 0 ? 'hoje' : d === 1 ? '1 dia' : d + ' dias'), titulo: 'relógio pausado — esperando o cliente' };
  }
  const ms = tempoEtapa(item), h = ms / 36e5;
  const cor = h >= 10 ? 'var(--at-crit)' : h >= 4 ? 'var(--at-warn)' : 'var(--at-muted)';
  return { cor, t: '⏱ ' + fmtDur(ms), titulo: 'tempo nesta etapa (horário comercial)' };
}

/* ══════════════════════════ equipe (responsáveis) ══════════════════════════
   Sem tabela própria: dono da conta (sem linha em usuarios_sistema) +
   usuários ativos cadastrados em Configurações > Usuários -- mesmo padrão
   do "euLinha" de Configuracoes.jsx, aqui usando o id real (auth uid) pra
   poder gravar em atendente_auth_id. */
function useEquipe() {
  const [equipe, setEquipe] = useState([]);
  useEffect(() => {
    const eu = usuarioLogado();
    listarUsuarios()
      .then(lista => {
        const euLinha = eu ? { id: eu.id, nome: eu.nome || eu.email || 'Você', setor: eu.setor || '' } : null;
        /* sub-usuário logado também aparece como linha própria em
           usuarios_sistema (criado_por = tenant) -- exclui pelo id pra não
           duplicar "eu" mais uma vez vindo da lista */
        const ativos = (lista || [])
          .filter(u => u.ativo && u.id !== eu?.id)
          .map(u => ({ id: u.id, nome: u.nome, setor: u.setor || '' }));
        setEquipe([euLinha, ...ativos].filter(Boolean));
      })
      .catch(() => {
        setEquipe(eu ? [{ id: eu.id, nome: eu.nome || eu.email, setor: eu.setor || '' }] : []);
      });
  }, []);
  return equipe;
}

/* ══════════════════════════ seletor de cliente ══════════════════════════ */

function ClientePicker({ value, onChange, placeholder, autoFocus }) {
  const [texto, setTexto]     = useState(value ? rotuloCliente(value) : '');
  const [aberto, setAberto]   = useState(false);
  const [clientes, setClientes] = useState([]);
  const ref = useRef(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setTexto(value ? rotuloCliente(value) : ''); }, [value?.id]);

  useEffect(() => {
    function fora(e) { if (ref.current && !ref.current.contains(e.target)) setAberto(false); }
    document.addEventListener('mousedown', fora);
    return () => document.removeEventListener('mousedown', fora);
  }, []);

  /* busca de novo a cada abertura -- a aba de Atendimento fica montada o
     tempo todo (o app não recarrega ao trocar de aba), então uma busca só
     no primeiro carregamento ficaria desatualizada se um cliente novo for
     cadastrado em Clientes depois. Assim sempre reflete o que está
     cadastrado no momento em que a pessoa vai escolher. */
  function abrir() {
    setAberto(true);
    listarClientes().then(setClientes).catch(() => {});
  }

  const filtrados = useMemo(() => {
    const q = norm(texto);
    if (!q) return clientes.slice(0, 40);
    const porCodigo = clientes.filter(c => norm(c.codigo).startsWith(q));
    const porNome    = clientes.filter(c => !porCodigo.includes(c) && norm(c.nome).includes(q));
    return [...porCodigo, ...porNome].slice(0, 40);
  }, [texto, clientes]);

  return (
    <div className={styles.picker} ref={ref}>
      <input
        value={texto}
        placeholder={placeholder}
        autoFocus={autoFocus}
        onChange={e => { setTexto(e.target.value); setAberto(true); if (value) onChange(null); }}
        onFocus={abrir}
      />
      {aberto && (
        <div className={styles.pickerLista}>
          {filtrados.length ? filtrados.map(c => (
            <button type="button" key={c.id} onClick={() => { onChange(c); setAberto(false); }}>
              <span className={styles.pickerCod}>{c.codigo}</span>{c.nome}
            </button>
          )) : <div className={styles.pickerVazio}>Nenhum cliente encontrado</div>}
        </div>
      )}
    </div>
  );
}
function rotuloCliente(c) { return c ? `${c.codigo ? c.codigo + ' - ' : ''}${c.nome}` : ''; }

/* ══════════════════════════ componente raiz ══════════════════════════ */

function Atendimento() {
  const [cards, setCards]           = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro]             = useState('');
  const equipe = useEquipe();

  const [filtroSetores, setFiltroSetores] = useState(new Set());
  const [busca, setBusca]                 = useState('');
  const [verTodosOk, setVerTodosOk]       = useState(false);

  const [capCliente, setCapCliente] = useState(null);
  const [capPedido, setCapPedido]   = useState('');
  const [capUrgente, setCapUrgente] = useState(false);
  const [capTip, setCapTip]         = useState('');

  const [editId, setEditId]     = useState(null);
  const [editForm, setEditForm] = useState(null);

  const [checklistId, setChecklistId] = useState(null);
  const [ck1, setCk1] = useState(false);
  const [ck2, setCk2] = useState(false);

  /* etapas (checklist interno) do card aberto no modal de detalhes --
     ligado ao editId, buscado junto quando o modal abre */
  const [etapasLista, setEtapasLista]     = useState([]);
  const [etapasCarregando, setEtapasCarregando] = useState(false);
  const [etapasTexto, setEtapasTexto]     = useState('');
  const [etapasErro, setEtapasErro]       = useState('');
  const [etapaEditandoId, setEtapaEditandoId]     = useState(null);
  const [etapaEditandoTexto, setEtapaEditandoTexto] = useState('');

  const [popover, setPopover] = useState(null); // { tipo: 'dono'|'etapa', id, x, y, pendenteEtapa? }
  const [, forcarTick] = useState(0);
  const arrastando = useRef(null);

  function carregar() {
    setCarregando(true);
    listarAtendimentos()
      .then(r => setCards(r.items || []))
      .catch(e => setErro(e.message))
      .finally(() => setCarregando(false));
  }
  useEffect(() => { carregar(); }, []);

  /* atualização silenciosa em segundo plano -- outra pessoa pode criar ou
     mover um atendimento nesse meio-tempo (mais de um atendente no mesmo
     board) e essa aba fica montada indefinidamente sem recarregar sozinha,
     então sem isso o board ficava desatualizado até um F5. Não mexe em
     "carregando" (sem piscar "carregando..." por cima dos cards) e pula a
     rodada se tiver um arrastar em andamento, pra não trocar o board por
     baixo do card que a pessoa está segurando. */
  useEffect(() => {
    const t = setInterval(() => {
      if (arrastando.current) return;
      listarAtendimentos().then(r => setCards(r.items || [])).catch(() => {});
    }, 15000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const t = setInterval(() => forcarTick(v => v + 1), 30000);
    return () => clearInterval(t);
  }, []);
  useEffect(() => {
    function fecharPop() { setPopover(null); }
    document.addEventListener('click', fecharPop);
    return () => document.removeEventListener('click', fecharPop);
  }, []);

  const pessoa = useCallback(authId => equipe.find(p => p.id === authId) || null, [equipe]);

  const setoresDisponiveis = useMemo(() => {
    const s = new Set();
    equipe.forEach(p => { if (p.setor) s.add(p.setor); });
    return [...s].sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }, [equipe]);

  function passaFiltro(item) {
    if (filtroSetores.size) {
      const setor = pessoa(item.atendente_auth_id)?.setor || '';
      const semDono = !item.atendente_auth_id;
      if (!((setor && filtroSetores.has(setor)) || (semDono && filtroSetores.has('__sem')))) return false;
    }
    if (busca && !norm(`${item.cliente_nome} ${item.pedido} ${item.observacoes || ''}`).includes(norm(busca))) return false;
    return true;
  }

  async function registrar() {
    if (!capCliente)      { setCapTip('Selecione um cliente da lista.'); return; }
    if (!capPedido.trim()) { setCapTip('Descreva o que o cliente pediu.'); return; }
    try {
      const novo = await criarAtendimento({ cliente_id: capCliente.id, pedido: capPedido.trim(), urgente: capUrgente });
      setCards(prev => [...prev, novo]);
      setCapCliente(null); setCapPedido(''); setCapUrgente(false);
      setCapTip(`✓ ${novo.cliente_nome} — ${novo.pedido}`);
    } catch (e) {
      setCapTip(e.message);
    }
  }

  async function mover(item, destino, atendenteId) {
    const donoFinal = atendenteId !== undefined ? atendenteId : item.atendente_auth_id;
    if (destino !== 'novo' && !donoFinal) {
      setPopover({ tipo: 'dono', id: item.id, x: null, y: null, pendenteEtapa: destino });
      return;
    }
    if (destino === 'ok') { abrirChecklist(item.id); return; }
    const fechamento = calcularFechamento(item);
    try {
      const atualizado = await moverAtendimento(item.id, {
        etapa: destino, ...fechamento,
        ...(atendenteId !== undefined ? { atendente_auth_id: atendenteId } : {}),
      });
      setCards(prev => prev.map(c => c.id === item.id ? atualizado : c));
    } catch (e) {
      setErro(e.message);
    }
  }

  /* escolher "sem dono" (atendenteId null) durante um movimento pendente
     só desatribui -- não força a mudança de etapa (mesma regra do
     protótipo: sem dono, o card não anda pra fora de "Novos") */
  async function definirDono(item, atendenteId, pendenteEtapa) {
    if (pendenteEtapa && atendenteId) {
      if (pendenteEtapa === 'ok') {
        try {
          const atualizado = await atualizarAtendimento(item.id, { atendente_auth_id: atendenteId });
          setCards(prev => prev.map(c => c.id === item.id ? atualizado : c));
        } catch (e) { setErro(e.message); }
        abrirChecklist(item.id);
        return;
      }
      await mover(item, pendenteEtapa, atendenteId);
      return;
    }
    try {
      const atualizado = await atualizarAtendimento(item.id, { atendente_auth_id: atendenteId });
      setCards(prev => prev.map(c => c.id === item.id ? atualizado : c));
    } catch (e) { setErro(e.message); }
  }

  function abrirChecklist(id) { setChecklistId(id); setCk1(false); setCk2(false); }
  async function confirmarResolucao() {
    if (!(ck1 && ck2)) return;
    const item = cards.find(c => c.id === checklistId);
    if (!item) return;
    const fechamento = calcularFechamento(item);
    try {
      const atualizado = await moverAtendimento(item.id, { etapa: 'ok', ...fechamento, resolvido_em: isoHoje() });
      setCards(prev => prev.map(c => c.id === item.id ? atualizado : c));
      setChecklistId(null);
    } catch (e) { setErro(e.message); }
  }

  /* abre o modal de detalhes e já traz o checklist de etapas junto --
     é onde dá pra editar/marcar/desmarcar/excluir/adicionar etapas */
  function abrirEditor(item) {
    setEditId(item.id);
    setEditForm({
      cliente: { id: item.cliente_id, codigo: item.cliente_codigo, nome: item.cliente_nome },
      pedido: item.pedido, urgente: !!item.urgente, atendente_auth_id: item.atendente_auth_id || '',
      observacoes: item.observacoes || '',
    });
    setEtapasTexto('');
    setEtapasErro('');
    setEtapaEditandoId(null);
    setEtapasCarregando(true);
    listarEtapasDoAtendimento(item.id)
      .then(r => setEtapasLista(r.items || []))
      .catch(err => setEtapasErro(err.message))
      .finally(() => setEtapasCarregando(false));
  }
  function fecharEditor() {
    setEditId(null); setEditForm(null);
    setEtapasLista([]); setEtapasTexto(''); setEtapasErro(''); setEtapaEditandoId(null);
  }
  async function salvarEditor() {
    if (!editForm.cliente || !editForm.pedido.trim()) return;
    try {
      const atualizado = await atualizarAtendimento(editId, {
        cliente_id: editForm.cliente.id, pedido: editForm.pedido.trim(), urgente: editForm.urgente,
        atendente_auth_id: editForm.atendente_auth_id || null, observacoes: editForm.observacoes,
      });
      setCards(prev => prev.map(c => c.id === editId ? { ...atualizado, ...resumoEtapas(etapasLista) } : c));
      fecharEditor();
    } catch (e) { setErro(e.message); }
  }
  async function excluirEditor() {
    if (!window.confirm('Excluir este atendimento?')) return;
    try {
      await excluirAtendimento(editId);
      setCards(prev => prev.filter(c => c.id !== editId));
      fecharEditor();
    } catch (e) { setErro(e.message); }
  }

  /* atualiza o resumo (total/concluídas/atual) do card na lista principal
     sempre que a lista de etapas de um atendimento muda -- pro card
     refletir na hora, sem esperar o próximo poll de 15s */
  function sincronizarResumoEtapas(atendimentoId, lista) {
    const resumo = resumoEtapas(lista);
    setCards(prev => prev.map(c => c.id === atendimentoId ? { ...c, ...resumo } : c));
  }

  async function adicionarNovasEtapas() {
    const itens = etapasTexto.split('\n').map(s => s.trim()).filter(Boolean);
    if (!itens.length) { setEtapasErro('Digite ao menos uma etapa.'); return; }
    try {
      const r = await adicionarEtapas(editId, itens);
      const lista = [...etapasLista, ...(r.items || [])];
      setEtapasLista(lista);
      setEtapasTexto('');
      setEtapasErro('');
      sincronizarResumoEtapas(editId, lista);
    } catch (e) { setEtapasErro(e.message); }
  }

  /* botão "Avançar" -- marca a etapa pendente atual (a primeira não
     concluída, na ordem) como concluída, igual o checkbox rápido do card,
     só que disponível também aqui dentro do modal de detalhes */
  async function avancarEtapaAtual() {
    const pendente = etapasLista.find(e => !e.concluida);
    if (!pendente) return;
    try {
      await alternarEtapa(pendente.id, true);
      const lista = etapasLista.map(x => x.id === pendente.id ? { ...x, concluida: true } : x);
      setEtapasLista(lista);
      sincronizarResumoEtapas(editId, lista);
    } catch (e) { setEtapasErro(e.message); }
  }

  async function alternarEtapaModal(etapa) {
    try {
      await alternarEtapa(etapa.id, !etapa.concluida);
      const lista = etapasLista.map(x => x.id === etapa.id ? { ...x, concluida: !etapa.concluida } : x);
      setEtapasLista(lista);
      sincronizarResumoEtapas(editId, lista);
    } catch (e) { setEtapasErro(e.message); }
  }

  async function excluirEtapaModal(etapa) {
    try {
      await excluirEtapa(etapa.id);
      const lista = etapasLista.filter(x => x.id !== etapa.id);
      setEtapasLista(lista);
      sincronizarResumoEtapas(editId, lista);
    } catch (e) { setEtapasErro(e.message); }
  }

  function iniciarEdicaoEtapa(etapa) { setEtapaEditandoId(etapa.id); setEtapaEditandoTexto(etapa.texto); }
  function cancelarEdicaoEtapa() { setEtapaEditandoId(null); setEtapaEditandoTexto(''); }
  async function salvarEdicaoEtapa(etapa) {
    const texto = etapaEditandoTexto.trim();
    if (!texto || texto === etapa.texto) { cancelarEdicaoEtapa(); return; }
    try {
      await editarTextoEtapa(etapa.id, texto);
      const lista = etapasLista.map(x => x.id === etapa.id ? { ...x, texto } : x);
      setEtapasLista(lista);
      sincronizarResumoEtapas(editId, lista);
    } catch (e) { setEtapasErro(e.message); }
    cancelarEdicaoEtapa();
  }

  /* marca a etapa atual como concluída direto pelo card, sem abrir o
     modal -- busca a lista fresca desse atendimento só pra recalcular o
     resumo (qual é a próxima etapa pendente). Pra desmarcar/editar, abre
     o modal de detalhes (ali o checklist inteiro fica visível). */
  async function concluirEtapaAtualDoCard(item, e) {
    e.stopPropagation();
    if (!item.etapa_atual_id) return;
    try {
      await alternarEtapa(item.etapa_atual_id, true);
      const r = await listarEtapasDoAtendimento(item.id);
      sincronizarResumoEtapas(item.id, r.items || []);
    } catch (err) { setErro(err.message); }
  }

  function abrirPopoverDono(id, e) {
    e.stopPropagation();
    const r = e.currentTarget.getBoundingClientRect();
    setPopover({ tipo: 'dono', id, x: r.left, y: r.bottom + window.scrollY + 4 });
  }
  function abrirPopoverEtapa(id, e) {
    e.stopPropagation();
    const r = e.currentTarget.getBoundingClientRect();
    setPopover({ tipo: 'etapa', id, x: Math.min(r.left - 140, window.innerWidth - 200), y: r.bottom + window.scrollY + 4 });
  }

  useEsc(fecharEditor, !!(editId && editForm)); // modal só fecha no Esc ou no X

  return (
    <div className={styles.wrap}>
      <div className={styles.capt}>
        <ClientePicker value={capCliente} onChange={setCapCliente} placeholder="Cliente" />
        <input
          className={styles.inpPedido} value={capPedido} placeholder="O que ele pediu" list="dlPedidosAtend"
          onChange={e => setCapPedido(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); registrar(); } }}
        />
        <datalist id="dlPedidosAtend">{PEDIDOS_SUGESTAO.map(p => <option key={p} value={p} />)}</datalist>
        <label className={`${styles.urg} ${capUrgente ? styles.urgOn : ''}`}>
          <input type="checkbox" checked={capUrgente} onChange={e => setCapUrgente(e.target.checked)} />urgente
        </label>
        <button type="button" className={styles.go} onClick={registrar}>Registrar</button>
      </div>
      {capTip && <div className={styles.qTip}>{capTip}</div>}

      <div className={styles.filtros}>
        <input className={styles.inpBusca} placeholder="Buscar cliente…" value={busca} onChange={e => setBusca(e.target.value)} />
        {setoresDisponiveis.map(s => (
          <button
            key={s} type="button"
            className={`${styles.chip} ${filtroSetores.has(s) ? styles.chipOn : ''}`}
            onClick={() => setFiltroSetores(prev => { const n = new Set(prev); n.has(s) ? n.delete(s) : n.add(s); return n; })}
          >
            <span className={styles.sw} style={{ background: corSetor(s) }} />{s}
          </button>
        ))}
        <button
          type="button"
          className={`${styles.chip} ${filtroSetores.has('__sem') ? styles.chipOn : ''}`}
          onClick={() => setFiltroSetores(prev => { const n = new Set(prev); n.has('__sem') ? n.delete('__sem') : n.add('__sem'); return n; })}
        >
          <span className={styles.sw} style={{ background: '#b7b5aa' }} />sem dono
        </button>
      </div>

      {erro && <p className={styles.erro}>{erro}</p>}

      <div className={styles.board}>
        {ETAPAS.map(etapa => {
          let itens = cards.filter(c => c.etapa === etapa.id && passaFiltro(c));
          if (etapa.id === 'ok') {
            if (!verTodosOk) itens = itens.filter(c => c.resolvido_em === isoHoje());
            itens = [...itens].sort((a, b) => (b.resolvido_em || '').localeCompare(a.resolvido_em || ''));
          } else {
            itens = [...itens].sort((a, b) => (b.urgente ? 1 : 0) - (a.urgente ? 1 : 0) || (a.aberto_em > b.aberto_em ? 1 : -1));
          }
          return (
            <div
              key={etapa.id}
              className={styles.col}
              onDragOver={e => e.preventDefault()}
              onDrop={e => {
                e.preventDefault();
                const item = cards.find(c => c.id === arrastando.current);
                if (item && item.etapa !== etapa.id) mover(item, etapa.id);
                arrastando.current = null;
              }}
            >
              <div className={styles.colhead}>
                <h2>{etapa.nome}</h2>
                <span className={styles.sub}>{etapa.id === 'ok' ? (verTodosOk ? 'todos' : 'hoje') : etapa.sub}</span>
                <span className={styles.count}>{itens.length}</span>
                {etapa.id === 'ok' && (
                  <button type="button" className={`${styles.eye} ${verTodosOk ? styles.eyeOn : ''}`} onClick={() => setVerTodosOk(v => !v)}>
                    {verTodosOk ? 'hoje' : 'todos'}
                  </button>
                )}
              </div>
              <div className={styles.drop}>
                {carregando ? <div className={styles.empty}>carregando…</div>
                  : itens.length ? itens.map(item => (
                    <Card
                      key={item.id} item={item} pessoa={pessoa}
                      onAbrir={() => abrirEditor(item)}
                      onDono={e => abrirPopoverDono(item.id, e)}
                      onEtapa={e => abrirPopoverEtapa(item.id, e)}
                      onDragStart={() => { arrastando.current = item.id; }}
                      verTodosOk={verTodosOk}
                      onAbrirEtapas={e => { e.stopPropagation(); abrirEditor(item); }}
                      onConcluirEtapaAtual={e => concluirEtapaAtualDoCard(item, e)}
                    />
                  )) : <div className={styles.empty}>vazio</div>}
              </div>
            </div>
          );
        })}
      </div>

      {popover && (
        <div
          className={styles.pop}
          style={popover.x != null ? { left: popover.x, top: popover.y } : { left: '50%', top: '50%', transform: 'translate(-50%,-50%)' }}
          onClick={e => e.stopPropagation()}
        >
          {popover.tipo === 'dono' ? (
            <>
              {equipe.map(p => (
                <button key={p.id} type="button" onClick={() => {
                  const item = cards.find(c => c.id === popover.id);
                  definirDono(item, p.id, popover.pendenteEtapa);
                  setPopover(null);
                }}>
                  <span className={styles.av} style={{ background: corSetor(p.setor) }}>{iniciais(p.nome)}</span>
                  {p.nome}<span className={styles.popSt}>{p.setor}</span>
                </button>
              ))}
              <button type="button" style={{ color: 'var(--at-muted)' }} onClick={() => {
                const item = cards.find(c => c.id === popover.id);
                definirDono(item, null, popover.pendenteEtapa);
                setPopover(null);
              }}>
                <span className={styles.av} style={{ background: 'transparent', border: '1px dashed var(--at-axis)', color: 'var(--at-muted)' }}>–</span>
                sem dono
              </button>
            </>
          ) : (
            ETAPAS.filter(e => e.id !== cards.find(c => c.id === popover.id)?.etapa).map(e => (
              <button key={e.id} type="button" onClick={() => {
                const item = cards.find(c => c.id === popover.id);
                mover(item, e.id);
                setPopover(null);
              }}>{e.nome}</button>
            ))
          )}
        </div>
      )}

      {editId && editForm && (
        <div className={styles.ov}>
          <div className={styles.modal}>
            <h3>Atendimento</h3>
            <div className={styles.fld}>
              <label>Cliente</label>
              <ClientePicker value={editForm.cliente} onChange={c => setEditForm(f => ({ ...f, cliente: c }))} placeholder="Cliente" />
            </div>
            <div className={styles.fld}>
              <label>Pedido</label>
              <input value={editForm.pedido} list="dlPedidosAtend" onChange={e => setEditForm(f => ({ ...f, pedido: e.target.value }))} />
            </div>
            <div className={styles.fld}>
              <label>Quem está atendendo</label>
              <select value={editForm.atendente_auth_id} onChange={e => setEditForm(f => ({ ...f, atendente_auth_id: e.target.value }))}>
                <option value="">— sem dono —</option>
                {equipe.map(p => <option key={p.id} value={p.id}>{p.nome}</option>)}
              </select>
            </div>
            <label className={styles.chkline}>
              <input type="checkbox" checked={editForm.urgente} onChange={e => setEditForm(f => ({ ...f, urgente: e.target.checked }))} />
              <span><b>Urgente.</b> Prazo legal, notificação ou cliente esperando resposta hoje.</span>
            </label>
            <div className={styles.tempos}>
              <div className={styles.tLb}>Tempo consumido</div>
              <LinhaTempo item={cards.find(c => c.id === editId)} etapaChave="novo" rotulo="Aguardando distribuição" />
              <LinhaTempo item={cards.find(c => c.id === editId)} etapaChave="transferido" rotulo="Transferido" />
              <LinhaTempo item={cards.find(c => c.id === editId)} etapaChave="atend" rotulo="Em atendimento" />
              <div className={styles.tLin}>
                <span>Esperando o cliente {cards.find(c => c.id === editId)?.etapa === 'aguard' && <span style={{ color: 'var(--at-warn)' }}>• pausado</span>}</span>
                <b style={{ color: 'var(--at-muted)' }}>{fmtDur(tempoEsperaAtual(cards.find(c => c.id === editId)))}</b>
              </div>
              <div className={`${styles.tLin} ${styles.tTot}`}>
                <span>Tempo do escritório</span><b>{fmtDur(tempoUtil(cards.find(c => c.id === editId) || { tempos: zerado() }))}</b>
              </div>
            </div>
            <div className={styles.fld}>
              <label>Anotações</label>
              <textarea rows={2} value={editForm.observacoes} placeholder="O que já foi feito, o que falta, o que foi cobrado…"
                onChange={e => setEditForm(f => ({ ...f, observacoes: e.target.value }))} />
            </div>

            <div className={styles.fld}>
              <label>Etapas</label>
              {!etapasCarregando && etapasLista.length > 0 && (
                (() => {
                  const pendente = etapasLista.find(e => !e.concluida);
                  return pendente ? (
                    <div className={styles.etapaAvancarLinha}>
                      <span>{pendente.texto}</span>
                      <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} onClick={avancarEtapaAtual}>
                        Avançar <FiChevronRight size={13} />
                      </button>
                    </div>
                  ) : (
                    <p className={styles.hint} style={{ color: 'var(--at-good)', margin: '0 0 8px' }}>
                      <FiCheck size={12} /> Todas as etapas concluídas
                    </p>
                  );
                })()
              )}
              {etapasCarregando ? (
                <p className={styles.hint}>Carregando…</p>
              ) : etapasLista.length > 0 && (
                <ul className={styles.etapasListaModal}>
                  {etapasLista.map(etapa => (
                    <li key={etapa.id} className={styles.etapaLinhaModal}>
                      <label>
                        <input type="checkbox" checked={etapa.concluida} onChange={() => alternarEtapaModal(etapa)} />
                        {etapaEditandoId === etapa.id ? (
                          <input
                            className={styles.etapaInputEdicao}
                            autoFocus
                            value={etapaEditandoTexto}
                            onClick={e => e.stopPropagation()}
                            onChange={e => setEtapaEditandoTexto(e.target.value)}
                            onBlur={() => salvarEdicaoEtapa(etapa)}
                            onKeyDown={e => {
                              if (e.key === 'Enter') { e.preventDefault(); salvarEdicaoEtapa(etapa); }
                              if (e.key === 'Escape') cancelarEdicaoEtapa();
                            }}
                          />
                        ) : (
                          <span className={etapa.concluida ? styles.etapaTextoConcluida : ''}>{etapa.texto}</span>
                        )}
                      </label>
                      <span className={styles.etapaAcoesModal}>
                        {etapaEditandoId !== etapa.id && (
                          <button type="button" className={styles.mv} onClick={() => iniciarEdicaoEtapa(etapa)} title="Editar texto">
                            <FiEdit2 size={12} />
                          </button>
                        )}
                        <button type="button" className={styles.mv} onClick={() => excluirEtapaModal(etapa)} title="Remover etapa">
                          <FiX size={13} />
                        </button>
                      </span>
                    </li>
                  ))}
                </ul>
              )}
              <textarea
                rows={2}
                value={etapasTexto}
                placeholder={'Adicionar etapas (uma por linha)'}
                onChange={e => setEtapasTexto(e.target.value)}
              />
              <button type="button" className={styles.btn} style={{ marginTop: 6 }} onClick={adicionarNovasEtapas}>
                <FiPlus size={13} /> Adicionar
              </button>
              {etapasErro && <p className={styles.erro}>{etapasErro}</p>}
            </div>

            <div className={styles.mfoot}>
              <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} onClick={salvarEditor}>Salvar</button>
              <button type="button" className={styles.btn} onClick={fecharEditor}>Cancelar</button>
              <div className={styles.spacer} />
              <button type="button" className={`${styles.btn} ${styles.btnDanger}`} onClick={excluirEditor}>Excluir</button>
            </div>
          </div>
        </div>
      )}

      {checklistId && (
        <div className={styles.ov}>
          <div className={styles.modal} style={{ maxWidth: 420 }}>
            <h3>Antes de resolver</h3>
            <p className={styles.hint}>
              <b>{cards.find(c => c.id === checklistId)?.cliente_nome}</b> — {cards.find(c => c.id === checklistId)?.pedido}
            </p>
            <label className={styles.chkline}>
              <input type="checkbox" checked={ck1} onChange={e => setCk1(e.target.checked)} />
              <span><b>Foi feito.</b> O que o cliente pediu está efetivamente concluído.</span>
            </label>
            <label className={styles.chkline}>
              <input type="checkbox" checked={ck2} onChange={e => setCk2(e.target.checked)} />
              <span><b>Cliente respondido.</b> Ele recebeu o retorno — não basta resolver por dentro.</span>
            </label>
            {!(ck1 && ck2) && <p className={styles.hint} style={{ margin: '0 0 10px' }}>Marque os dois itens acima pra habilitar o botão Resolver.</p>}
            <div className={styles.mfoot}>
              <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} onClick={confirmarResolucao} disabled={!(ck1 && ck2)}>Resolver</button>
              <button type="button" className={styles.btn} onClick={() => setChecklistId(null)}>Cancelar</button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

function tempoEsperaAtual(item) {
  if (!item) return 0;
  const base = item.tempos?.aguard || 0;
  if (item.etapa === 'aguard' && item.etapa_desde) return base + (Date.now() - new Date(item.etapa_desde).getTime());
  return base;
}
function LinhaTempo({ item, etapaChave, rotulo }) {
  if (!item) return null;
  const rodando = item.etapa === etapaChave ? correndo(item) : 0;
  const total = (item.tempos?.[etapaChave] || 0) + rodando;
  return (
    <div className={styles.tLin}>
      <span>{rotulo}{item.etapa === etapaChave && <span style={{ color: 'var(--at-good)' }}> • agora</span>}</span>
      <b>{fmtDur(total)}</b>
    </div>
  );
}

function Card({ item, pessoa, onAbrir, onDono, onEtapa, onDragStart, verTodosOk, onAbrirEtapas, onConcluirEtapaAtual }) {
  const dono = pessoa(item.atendente_auth_id);
  const corSetorCard = dono ? corSetor(dono.setor) : '#b7b5aa';
  /* card urgente (ainda não resolvido) destaca a lateral na mesma cor da
     bolinha de urgente, em vez da cor do setor -- prioridade visual */
  const urgenteAtivo = item.urgente && item.etapa !== 'ok';
  const corEdge = urgenteAtivo ? 'var(--at-crit)' : corSetorCard;
  const relI = relogioInfo(item);
  const totalEtapas = item.etapas_total || 0;
  const etapasPct = totalEtapas ? Math.round(((item.etapas_concluidas || 0) / totalEtapas) * 100) : 0;
  return (
    <div className={styles.card} draggable onDragStart={onDragStart} onClick={onAbrir}>
      <div className={styles.edge} style={{ background: corEdge }} />
      <div className={styles.cli}>
        {urgenteAtivo && <span className={styles.u} title="urgente" />}
        {item.cliente_nome}
      </div>
      <div className={styles.tit}>
        {item.pedido}
        {item.etapa === 'ok' && verTodosOk && item.resolvido_em !== isoHoje() && (
          <span style={{ color: 'var(--at-muted)' }}> · {item.resolvido_em?.slice(8, 10)}/{item.resolvido_em?.slice(5, 7)}</span>
        )}
      </div>

      {totalEtapas > 0 ? (
        <div className={styles.etapasBloco}>
          {item.etapa_atual ? (
            <label className={styles.etapaAtualLinha} onClick={e => e.stopPropagation()}>
              <input type="checkbox" onChange={onConcluirEtapaAtual} />
              <span>{item.etapa_atual}</span>
            </label>
          ) : (
            <div className={styles.etapaAtualLinha}><FiCheck size={12} color="var(--at-good)" /><span>Todas as etapas concluídas</span></div>
          )}
          <div className={styles.etapasBarraLinha}>
            <div className={styles.etapasBarra}><div className={styles.etapasBarraFill} style={{ width: `${etapasPct}%` }} /></div>
            <button type="button" className={styles.etapasContagem} onClick={onAbrirEtapas} title="Ver/editar etapas">
              {item.etapas_concluidas || 0}/{totalEtapas}
            </button>
          </div>
        </div>
      ) : (
        <button type="button" className={styles.etapasAdd} onClick={onAbrirEtapas}>
          <FiPlus size={11} /> etapas
        </button>
      )}

      <div className={styles.foot}>
        <button type="button" className={styles.who} onClick={onDono}>
          {dono
            ? <><span className={styles.av} style={{ background: corSetorCard }}>{iniciais(dono.nome)}</span>{dono.nome.split(' ')[0]}</>
            : <><span className={styles.av} style={{ background: 'transparent', border: '1px dashed var(--at-axis)', color: 'var(--at-muted)' }}>+</span><span style={{ color: 'var(--at-muted)' }}>atribuir</span></>}
        </button>
        <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
          <span className={styles.age} title={relI.titulo} style={{ color: relI.cor }}>{relI.t}</span>
          <button type="button" className={styles.mv} title="Mover para outra coluna" onClick={onEtapa}><FiChevronRight size={13} /></button>
        </span>
      </div>
    </div>
  );
}

export default Atendimento;
